import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { CityAutocomplete } from "@/components/CityAutocomplete";

export default function ProProfil() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [zoneInput, setZoneInput] = useState("");
  const [contract, setContract] = useState(null);
  const [commissionPct, setCommissionPct] = useState(15);

  useEffect(() => {
    api
      .get("/school/profile")
      .then(({ data }) => {
        setForm({
          name: data.name || "",
          phone: data.phone || "",
          address: data.address || "",
          city: data.city || "",
          postal_code: data.postal_code || "",
          coverage: data.coverage || "",
          zones: data.zones || [],
          price_permis_b: data.price_permis_b ?? "",
          description: data.description || "",
          photos: data.photos || [],
        });
        setContract({ note: data.contract_note });
        setCommissionPct(
          data.commission_rate != null && data.commission_rate !== ""
            ? Math.round(Number(data.commission_rate) * 100)
            : 15
        );
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || e.message));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/school/profile", {
        ...form,
        price_permis_b: form.price_permis_b === "" ? null : Number(form.price_permis_b),
      });
      toast.success("Fiche mise à jour.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Shell role="school"><p className="p-10 text-neutral-500">Chargement…</p></Shell>;

  const input = (key, placeholder, type = "text") => (
    <input
      data-testid={`profile-${key.replace("_", "-")}-input`}
      type={type}
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 outline-none focus:border-brand transition-colors"
    />
  );

  return (
    <Shell role="school">
      <div className="px-5 py-8 sm:px-10 max-w-3xl">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Ma fiche auto-école</h1>
        <p className="mt-2 text-neutral-500">
          Votre <strong>ville</strong> détermine dans quelle sélection vos offres de permis accéléré apparaissent aux
          élèves. Renseignez-la précisément, ainsi que votre forfait de référence (base de calcul de la commission).
        </p>

        <div className="mt-8 space-y-4">
          {input("name", "Nom de l'auto-école")}
          {input("address", "Adresse")}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <CityAutocomplete
                testId="profile-city-input"
                value={form.city}
                onChange={(v) => set("city", v)}
                onSelect={(c) =>
                  setForm((f) => ({ ...f, city: c.nom, postal_code: f.postal_code || (c.codesPostaux || [])[0] || "" }))
                }
                placeholder="Ville de l'auto-école"
              />
            </div>
            {input("postal_code", "Code postal")}
          </div>
          {input("phone", "Téléphone")}
          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <p className="text-sm font-semibold">Communes desservies</p>
            <p className="text-xs text-neutral-500">
              Indiquez les communes autour de votre centre où vous accueillez des élèves. C'est une information
              utile pour le réseau et pour orienter les futurs élèves vers la bonne zone.
            </p>
            <div className="mt-3 flex gap-2">
              <div className="flex-1">
                <CityAutocomplete
                  testId="profile-zone-input"
                  value={zoneInput}
                  onChange={setZoneInput}
                  onSelect={(c) => {
                    if (!form.zones.includes(c.nom)) set("zones", [...form.zones, c.nom]);
                    setZoneInput("");
                  }}
                  placeholder="Commencez à taper une commune…"
                />
              </div>
              <button
                data-testid="profile-add-zone-btn"
                onClick={() => {
                  const z = zoneInput.trim();
                  if (!z || form.zones.includes(z)) return;
                  set("zones", [...form.zones, z]);
                  setZoneInput("");
                }}
                className="rounded-md bg-neutral-900 px-4 text-sm font-bold text-white transition-colors hover:bg-brand"
              >
                Ajouter
              </button>
            </div>
            {form.zones.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-zones-list">
                {form.zones.map((z) => (
                  <span
                    key={z}
                    className="inline-flex items-center gap-2 rounded-full border border-brand bg-brand/5 px-3 py-1.5 text-sm font-semibold"
                  >
                    {z}
                    <button
                      data-testid={`profile-remove-zone-${z.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => set("zones", form.zones.filter((x) => x !== z))}
                      className="text-neutral-400 hover:text-red-600 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {input("coverage", "Précisions sur la zone (facultatif)")}
          <div>
            {input("price_permis_b", "Prix de référence du forfait permis accéléré (€)", "number")}
            <p className="mt-1 text-xs text-neutral-500">
              Sert de base par défaut au calcul de la commission. Vos prix réels par offre se définissent dans
              « Mes formules ».
            </p>
          </div>
          <textarea
            data-testid="profile-description-input"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={5}
            placeholder="Description de votre auto-école, points forts, horaires…"
            className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 outline-none focus:border-brand transition-colors"
          />

          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <p className="text-sm font-semibold">Photos (URL)</p>
            <div className="mt-3 flex gap-2">
              <input
                data-testid="profile-photo-url-input"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="https://…"
                className="flex-1 rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-brand transition-colors"
              />
              <button
                data-testid="profile-add-photo-btn"
                onClick={() => {
                  if (!photoUrl.trim()) return;
                  set("photos", [...form.photos, photoUrl.trim()]);
                  setPhotoUrl("");
                }}
                className="rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand"
              >
                Ajouter
              </button>
            </div>
            {form.photos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.photos.map((p, i) => (
                  <div key={p} className="relative">
                    <img src={p} alt="" className="h-24 w-full rounded-md object-cover" />
                    <button
                      data-testid={`profile-remove-photo-${i}`}
                      onClick={() => set("photos", form.photos.filter((_, j) => j !== i))}
                      className="absolute right-1 top-1 rounded bg-white/90 px-2 text-xs font-bold text-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-2 border-brand/30 rounded-lg bg-brand/5 p-5" data-testid="commission-info-panel">
            <p className="text-sm font-bold">Votre commission réseau</p>
            <p className="mt-1 text-xs text-neutral-600">
              Ce taux est <strong>défini par Plan Permis</strong> selon votre accord de partenariat. Vous ne pouvez pas
              le modifier vous-même. La commission se calcule sur le <strong>montant du forfait réellement signé avec
              l'élève</strong>, uniquement pour un élève effectivement <strong>inscrit</strong> via Plan Permis.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <span
                data-testid="commission-rate-badge"
                className="grid h-12 w-12 place-items-center rounded-full bg-brand text-base font-black text-white"
              >
                {commissionPct}%
              </span>
              <div className="text-sm">
                <p className="font-bold text-neutral-800">{commissionPct}% du forfait</p>
                {form.price_permis_b !== "" && Number(form.price_permis_b) > 0 && (
                  <p className="text-neutral-500">
                    soit ~{(Number(form.price_permis_b) * (commissionPct / 100)).toFixed(0)} € par élève inscrit
                  </p>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-neutral-500">
              Vous ne réglez rien tant qu'un élève ne s'est pas inscrit. Renseignez bien votre forfait ci-dessus pour un
              calcul juste. Pour renégocier votre taux, contactez Plan Permis.
            </p>
            {contract?.note && <p className="mt-2 text-xs text-neutral-500">{contract.note}</p>}
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="profile-save-btn"
            className="rounded-full bg-brand px-8 py-3.5 font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer ma fiche"}
          </button>
        </div>
      </div>
    </Shell>
  );
}
